// src/components/ConditionsModal.js
// Formulaire de saisie des conditions d'un site (réutilisable : Communauté + historique Plongées).
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchWithAuth } from "../utils/api";
import "./ConditionsModal.css";

const DEPTH_BANDS = ["0-10", "10-20", "20-30", "30-40", "40+"];
// Rampe "clarté de l'eau" : trouble/brun → bleu net
const LEVEL_COLORS = { 1: "#8a7a5c", 2: "#6f9e86", 3: "#3fa7b8", 4: "#1c9fd6", 5: "#0077b6" };
// Distances indicatives (pour que tout le monde note la même chose)
const LEVEL_RANGES = { 1: "<1m", 2: "1-3m", 3: "3-6m", 4: "6-10m", 5: ">10m" };

export default function ConditionsModal({ onClose, onSaved, apiUrl, initialSite = null, initialDate = null }) {
    const { t } = useTranslation();
    const today = useMemo(() => new Date().toISOString().split("T")[0], []);

    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState(initialSite);
    const [siteSearch, setSiteSearch] = useState("");
    const [pickingSite, setPickingSite] = useState(!initialSite);

    const [date, setDate] = useState(initialDate || today);
    const [visibility, setVisibility] = useState({}); // { "0-10": 3, ... }
    const [temp, setTemp] = useState("");
    const [note, setNote] = useState("");

    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null); // { type: "error"|"success", msg }

    // Charge la liste des sites pour le sélecteur
    useEffect(() => {
        let cancelled = false;
        fetchWithAuth(`${apiUrl}/diving-sites`)
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => { if (!cancelled) setSites(Array.isArray(data) ? data : []); })
            .catch(() => { /* silencieux */ });
        return () => { cancelled = true; };
    }, [apiUrl]);

    const setBand = (band, level) => {
        setVisibility((v) => {
            const next = { ...v };
            if (next[band] === level) delete next[band]; // re-clic = désélection
            else next[band] = level;
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus(null);
        if (!selectedSite?.id) { setStatus({ type: "error", msg: t("conditions.needSite") }); return; }
        const hasVis = Object.keys(visibility).length > 0;
        const hasTemp = temp !== "" && !Number.isNaN(Number(temp));
        if (!hasVis && !hasTemp) { setStatus({ type: "error", msg: t("conditions.needData") }); return; }

        setSaving(true);
        try {
            const res = await fetchWithAuth(`${apiUrl}/site-conditions`, {
                method: "POST",
                body: JSON.stringify({
                    site_id: selectedSite.id,
                    observed_date: date,
                    water_temp: hasTemp ? Number(temp) : null,
                    visibility,
                    note: note.trim() || null,
                }),
            });
            if (!res.ok) throw new Error();
            setStatus({ type: "success", msg: t("conditions.success") });
            window.dispatchEvent(new Event("badges-check")); // un badge conditions vient p-e d'être débloqué
            if (onSaved) onSaved();
            setTimeout(() => onClose && onClose(), 900);
        } catch {
            setStatus({ type: "error", msg: t("conditions.error") });
        } finally {
            setSaving(false);
        }
    };

    const filteredSites = useMemo(() => {
        const term = siteSearch.trim().toLowerCase();
        return (Array.isArray(sites) ? sites : [])
            .filter((s) => s?.name && (!term || s.name.toLowerCase().includes(term)))
            .sort((a, b) => a.name.localeCompare(b.name))
            .slice(0, 40);
    }, [sites, siteSearch]);

    return (
        <div className="cond-overlay" onClick={onClose}>
            <div className="cond-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="cond-header">
                    <h2>{t("conditions.title")}</h2>
                    <button className="cond-close" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <form className="cond-body" onSubmit={handleSubmit}>
                    {/* Site */}
                    <label className="cond-label">{t("conditions.site")}</label>
                    {selectedSite && !pickingSite ? (
                        <div className="cond-site-selected">
                            <span>{selectedSite.name}</span>
                            <button type="button" onClick={() => { setPickingSite(true); setSiteSearch(""); }}>
                                {t("map.modal.changeSite")}
                            </button>
                        </div>
                    ) : (
                        <>
                            <input
                                autoFocus
                                type="text"
                                className="cond-input"
                                placeholder={t("conditions.searchSite")}
                                value={siteSearch}
                                onChange={(e) => setSiteSearch(e.target.value)}
                            />
                            <ul className="cond-site-list">
                                {filteredSites.length === 0 ? (
                                    <li className="cond-site-empty">{t("create.noResults")}</li>
                                ) : filteredSites.map((s) => (
                                    <li key={s.id}>
                                        <button type="button" onClick={() => { setSelectedSite(s); setPickingSite(false); setSiteSearch(""); }}>
                                            <span>{s.name}</span>
                                            {s.canton && <span className="cond-site-canton">{s.canton}</span>}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {/* Date */}
                    <label className="cond-label">{t("conditions.date")}</label>
                    <input type="date" className="cond-input" value={date} max={today} onChange={(e) => setDate(e.target.value)} />

                    {/* Visibilité par profondeur */}
                    <label className="cond-label">{t("conditions.visibilityTitle")}</label>
                    <p className="cond-hint">{t("conditions.visibilityHint")}</p>
                    <div className="cond-vis">
                        {DEPTH_BANDS.map((band) => (
                            <div className="cond-vis-row" key={band}>
                                <span className="cond-vis-band">{band === "40+" ? "40+ m" : `${band} m`}</span>
                                <div className="cond-vis-levels">
                                    {[1, 2, 3, 4, 5].map((lvl) => {
                                        const active = visibility[band] === lvl;
                                        return (
                                            <button
                                                type="button"
                                                key={lvl}
                                                className={`cond-chip${active ? " active" : ""}`}
                                                style={active ? { background: LEVEL_COLORS[lvl], borderColor: LEVEL_COLORS[lvl], color: "#fff" } : { color: LEVEL_COLORS[lvl], borderColor: LEVEL_COLORS[lvl] }}
                                                onClick={() => setBand(band, lvl)}
                                                title={`${t(`conditions.levels.${lvl}`)} (${LEVEL_RANGES[lvl]})`}
                                                aria-label={`${band} m : ${t(`conditions.levels.${lvl}`)} (${LEVEL_RANGES[lvl]})`}
                                            >
                                                {lvl}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="cond-legend">
                        {[1, 2, 3, 4, 5].map((lvl) => (
                            <span key={lvl} className="cond-legend-item">
                                <span className="cond-legend-dot" style={{ background: LEVEL_COLORS[lvl] }} />
                                {lvl} · {t(`conditions.levels.${lvl}`)} ({LEVEL_RANGES[lvl]})
                            </span>
                        ))}
                    </div>

                    {/* Température */}
                    <label className="cond-label">{t("conditions.temp")}</label>
                    <input type="number" step="0.5" className="cond-input" value={temp} onChange={(e) => setTemp(e.target.value)} placeholder="—" />

                    {/* Note */}
                    <label className="cond-label">{t("conditions.note")}</label>
                    <textarea className="cond-input cond-textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("conditions.notePlaceholder")} />

                    {status && <div className={`cond-status ${status.type}`}>{status.msg}</div>}

                    <button type="submit" className="cond-submit" disabled={saving}>
                        {saving ? t("conditions.submitting") : t("conditions.submit")}
                    </button>
                </form>
            </div>
        </div>
    );
}
